import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useApp } from '../../contexts/AppContext';
import { validateEmail, validatePassword } from '../../utils/validation';
import './Auth.css';

// Google icon SVG
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function LoginPage() {
  const supabase = useSupabaseClient();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { signIn } = useApp();
  const navigate = useNavigate();

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        return validateEmail(value);
      case 'password':
        return validatePassword(value);
      default:
        return null;
    }
  };

  const handleChange = (name, value) => {
    setFieldErrors(prev => ({ ...prev, [name]: null }));
    setError('');
    setFormData(prev => ({ ...prev, [name]: value }));

    const fieldError = validateField(name, value);
    if (fieldError) {
      setFieldErrors(prev => ({ ...prev, [name]: fieldError }));
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();

    const allErrors = {};
    Object.entries(formData).forEach(([name, value]) => {
      const error = validateField(name, value);
      if (error) allErrors[name] = error;
    });

    if (Object.keys(allErrors).length > 0) {
      setFieldErrors(allErrors);
      setError('נא לתקן את כל השגיאות לפני הכניסה');
      return;
    }

    try {
      setError('');
      setLoading(true);
      const result = await signIn(formData.email, formData.password);
      if (result?.error) {
        throw result.error;
      }

      // Navigate to overview — AuthenticatedLayout will redirect to wizard if no projects exist
      navigate('/overview');
    } catch (error) {
      console.error('Login error:', error);
      if (error.message?.includes('Invalid login credentials')) {
        setError('סיסמה שגויה או משתמש לא קיים');
      } else if (error.message?.includes('Email not confirmed')) {
        setError('יש לאשר את כתובת הדוא"ל לפני הכניסה');
      } else {
        setError('שגיאה בכניסה למערכת. נסה שוב');
      }
    } finally {
      setLoading(false);
    }
  }

  // Handle password reset
  const handleResetPassword = async () => {
    if (!resetEmail) {
      setError('נא להזין כתובת אימייל');
      return;
    }
    const emailError = validateEmail(resetEmail);
    if (emailError) {
      setError(emailError);
      return;
    }

    try {
      setResetLoading(true);
      setError('');
      const redirectUrl =
        process.env.NODE_ENV === 'production'
          ? 'https://www.kamuyot.com/reset-password'
          : `${window.location.origin}/reset-password`;

      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: redirectUrl,
      });
      if (error) throw error;
      setResetSent(true);
    } catch (err) {
      console.error('Reset password error:', err);
      setError('שגיאה בשליחת קישור איפוס. נסה שוב');
    } finally {
      setResetLoading(false);
    }
  };

  // Handle Google OAuth login
  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);
      setError('');
      const redirectUrl =
        process.env.NODE_ENV === 'production'
          ? 'https://www.kamuyot.com/auth/callback'
          : `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        },
      });
      if (error) throw error;
    } catch (err) {
      console.error('Google login error:', err);
      setError('שגיאה בהתחברות עם Google');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="auth-container login-background">
      <div className="auth-box">
        <h2>התחברות</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>אימייל</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => handleChange('email', e.target.value)}
              required
            />
            {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
          </div>
          <div className="form-group password-input-container">
            <label>סיסמה</label>
            <div className="input-with-icon">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={e => handleChange('password', e.target.value)}
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={handleClickShowPassword}
                className="password-toggle"
                aria-label="toggle password visibility"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
          </div>
          <button disabled={loading} type="submit" className="login-button">
            {loading ? 'מתחבר...' : 'התחבר'}
          </button>
        </form>

        <div className="forgot-password-link">
          <button type="button" onClick={() => setShowReset(!showReset)}>
            שכחתי סיסמה
          </button>
        </div>

        {showReset && (
          <div className="reset-section">
            {resetSent ? (
              <div className="reset-success">קישור איפוס נשלח לאימייל שלך. בדוק את תיבת הדואר.</div>
            ) : (
              <>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  placeholder="הזן את כתובת האימייל"
                  className="reset-email-input"
                />
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={resetLoading}
                  className="reset-button"
                >
                  {resetLoading ? 'שולח...' : 'שלח קישור איפוס'}
                </button>
              </>
            )}
          </div>
        )}

        <div className="auth-divider">
          <span>או</span>
        </div>

        <button
          type="button"
          className="google-button"
          onClick={handleGoogleLogin}
          disabled={googleLoading}
        >
          <GoogleIcon />
          {googleLoading ? 'מתחבר...' : 'התחבר עם Google'}
        </button>

        <div className="auth-link">
          אין לך חשבון? <Link to="/signup">הרשם כאן</Link>
        </div>
      </div>
    </div>
  );
}
